import { useTranslation } from 'react-i18next';
import type { Facet, Kolom } from '@xxtract/tabelfilters';
import { isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { DatumFilter, KeuzelijstFilter, TekstFilter } from '@/components/tabel';
import { CONTROL_CLASS } from '@/components/tabel/stijl';
import { SERVICE_STATUSES } from '../../orders/orders.api.constants';
import { PROCESS_FILTER_VELDEN, PROCESSEN_FILTER_LIJST, type ProcessFilterValues } from '../procesLijsten';

interface ProcessFiltersProps {
  values: ProcessFilterValues;
  defaultStilstandDrempelWerkdagen: number;
  onChange: (patch: Partial<ProcessFilterValues>) => void;
}

const kolom = (sleutel: string) => PROCESSEN_FILTER_LIJST.kolommen.find((k) => k.sleutel === sleutel) as Kolom<never, never>;
const KLANT = kolom('gln');
const STATUS = kolom('status');
const PERIODE = kolom('periode');
/** De balk kent geen aantallen: vaste statussen met aantal 0 (CAP-10). */
const STATUS_KEUZES: Facet[] = SERVICE_STATUSES.map((waarde) => ({ waarde, aantal: 0 }));
const GEEN_SUGGESTIES = () => ({ waarden: [], meer: false });
const UITLEG_ID = 'process-filters-status-uitleg';

/**
 * spec-tabelfilters story 12: de Processen-paginafilter (klant, status, periode, stilstanddrempel) uit de
 * centrale bouwstenen. Een eigen rij boven de inhoud die niet inklapt (CAP-13); de stand staat in de
 * gedeelde lijststand `processen` (`useProcessenFilter`). `gln` is een queryfilter, geen persoonsgegeven (NFR4).
 */
export function ProcessFilters({ values, defaultStilstandDrempelWerkdagen, onChange }: ProcessFiltersProps) {
  const { t } = useTranslation();
  const statusGekozen = values.status != null;
  const drempel = values.stilstandDrempelWerkdagen;

  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="process-filters">
      {/* De KlantZoeker als instelling van het tekstfilter: pas filteren na een keuze, vanaf twee tekens. */}
      <TekstFilter kolom={KLANT} onChange={(gln) => onChange({ gln: gln || undefined })} suggesties={GEEN_SUGGESTIES} tekst={values.gln ?? ''} />

      <KeuzelijstFilter
        beschrijvingId={statusGekozen ? UITLEG_ID : undefined}
        gekozen={values.status ? [values.status] : []}
        keuzes={STATUS_KEUZES}
        kolom={STATUS}
        onChange={(gekozen) => onChange({ status: gekozen.find((w) => w !== null) ?? undefined })}
        zonderAantallen
      />

      <DatumFilter bereik={{ van: values.from, tot: values.to }} compact kolom={PERIODE} onChange={(b) => onChange({ from: b.van, to: b.tot })} testId="process-filters-periode" />

      <label className="flex items-center gap-2 text-xs text-[var(--foreground-muted)]">
        {t('processes.filters.stilstandDrempel')}
        {/* Toont altijd de effectieve waarde (standaard 5); leegmaken zet hem terug op de standaard, een niet-getal toont leeg. */}
        <input
          aria-label={t('processes.filters.stilstandDrempel')}
          className={`${CONTROL_CLASS} w-20`}
          max={90}
          min={1}
          onChange={(event) => onChange({ stilstandDrempelWerkdagen: event.target.value === '' ? undefined : Number(event.target.value) })}
          type="number"
          value={drempel === undefined ? defaultStilstandDrempelWerkdagen : Number.isFinite(drempel) ? drempel : ''}
        />
      </label>

      {/* De bron filtert op dienststatus, niet op de dienst die dit scherm toont: zeg dat zodra er een status gekozen is. */}
      {statusGekozen ? (
        <p className="basis-full text-[11px] text-[var(--info-text)]" data-testid="process-filters-status-uitleg" id={UITLEG_ID}>{t('processes.filters.statusUitleg')}</p>
      ) : null}
    </div>
  );
}

/** Voorvoegsel voor de testids van de geweigerd-filtermelding (`processes-filter-rejected`, `-clear`). */
export const processFilterTestId = 'processes';

export const PROCESS_FILTER_TEKSTEN = { melding: 'processes.filters.geweigerd', velden: 'processes.filters.geweigerdVelden', wissen: 'processes.filters.wissen' } as const;

/** Processen kent altijd een bronstatus in het antwoord: hier is hij verplicht, zodat een nieuwe pagina hem niet stil vergeet. */
export function isProcessFilterRejected(error: unknown, bronStatus: string | undefined): boolean {
  return isFilterRejected(error, PROCESS_FILTER_VELDEN, bronStatus);
}
